import { useEffect, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import ClientEditModal from "@/components/ClientEditModal";
import { api, apiError } from "@/lib/api";

const blankClient = {
  nombre: "",
  ci: "",
  celular: "",
  correo: "",
  ciudad: "",
  observaciones: "",
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(blankClient);
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [editingClient, setEditingClient] = useState(null);

  const loadClients = () =>
    api.get("/clients").then((response) => setClients(response.data));

  useEffect(() => {
    loadClients();
  }, []);

  const change = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const saveClient = async (event) => {
    event.preventDefault();
    setMessage("");

    try {
      await api.post("/clients", form);
      setForm(blankClient);
      setMessage("Cliente guardado correctamente.");
      loadClients();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const deleteClient = async (client) => {
    const confirmed = window.confirm(
      `¿Estás seguro de eliminar al cliente "${client.nombre}"?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmed) return;

    setMessage("");

    try {
      await api.delete(`/clients/${client.id}`);

      setClients((current) =>
        current.filter((item) => item.id !== client.id)
      );

      setMessage("Cliente eliminado correctamente.");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const filtered = clients.filter((client) =>
    `${client.nombre} ${client.ci}`
      .toLowerCase()
      .includes(query.toLowerCase())
  );

  const updateClient = (updatedClient) => {
    setClients((current) =>
      current.map((client) =>
        client.id === updatedClient.id ? updatedClient : client
      )
    );

    setEditingClient(null);
    setMessage("Cliente actualizado correctamente.");
  };

  return (
    <>
      <div className="page-header">
        <div>
          <span className="eyebrow">CARTERA COMERCIAL</span>
          <h1>Clientes</h1>
          <p>Centraliza sus datos antes de preparar una cotización.</p>
        </div>
      </div>

      <div className="clients-layout">
        <form className="client-form" onSubmit={saveClient}>
          <div className="panel-heading">
            <h2>
              <Plus size={20} />
              Nuevo cliente
            </h2>
          </div>

          <label>
            Nombre completo
            <input
              name="nombre"
              onChange={change}
              required
              value={form.nombre}
            />
          </label>

          <div className="form-two-columns">
            <label>
              CI
              <input
                name="ci"
                onChange={change}
                required
                value={form.ci}
              />
            </label>

            <label>
              Celular
              <input
                name="celular"
                onChange={change}
                value={form.celular}
              />
            </label>
          </div>

          <label>
            Correo
            <input
              name="correo"
              onChange={change}
              type="email"
              value={form.correo}
            />
          </label>

          <label>
            Ciudad
            <input
              name="ciudad"
              onChange={change}
              value={form.ciudad}
            />
          </label>

          <label>
            Observaciones
            <textarea
              name="observaciones"
              onChange={change}
              value={form.observaciones}
            />
          </label>

          {message && <p className="form-note">{message}</p>}

          <button className="primary-button" type="submit">
            Guardar cliente
          </button>
        </form>

        <section className="clients-table-panel">
          <div className="table-toolbar">
            <h2>
              Directorio <span>{clients.length}</span>
            </h2>

            <label className="search-field">
              <Search size={17} />
              <input
                data-testid="clients-search-input"
                id="clients-search"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar cliente o CI"
                value={query}
              />
            </label>
          </div>

          <div className="data-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>CI</th>
                  <th>Celular</th>
                  <th>Ciudad</th>
                  <th>Creado por</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((client) => (
                  <tr
                    data-testid={`client-row-${client.id}`}
                    key={client.id}
                  >
                    <td>
                      {client.nombre}
                      <small>{client.correo || "Sin correo"}</small>
                    </td>

                    <td>{client.ci}</td>

                    <td>{client.celular}</td>

                    <td>{client.ciudad || "—"}</td>

                    <td data-testid={`client-owner-${client.id}`}>
                      {client.created_by_name || "Registro histórico"}
                    </td>

                    <td>
                      <div className="client-row-actions">
                        <button
                          aria-label={`Editar ${client.nombre}`}
                          className="icon-button client-edit-button"
                          data-testid={`edit-client-${client.id}`}
                          onClick={() => setEditingClient(client)}
                          title="Editar cliente"
                          type="button"
                        >
                          <Pencil size={17} />
                        </button>

                        <button
                          aria-label={`Eliminar ${client.nombre}`}
                          className="icon-button danger client-delete-button"
                          data-testid={`delete-client-${client.id}`}
                          onClick={() => deleteClient(client)}
                          title="Eliminar cliente"
                          type="button"
                        >
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!filtered.length && (
                  <tr>
                    <td colSpan="6">
                      No hay clientes para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {editingClient && (
        <ClientEditModal
          client={editingClient}
          onClose={() => setEditingClient(null)}
          onSaved={updateClient}
        />
      )}
    </>
  );
}